import { queryOptions, keepPreviousData } from "@tanstack/react-query";
import type {
  DiscoveryFilters,
  Influencer,
  PaginationState,
} from "@/marketing/models/influencer";
import { INFLUENCER_KEYS } from "./keys";
import {
  influencerAPIClient,
  type CreateInfluencerParams,
} from "../influencerAPIClient";

export const INFLUENCER_QUERIES = {
  search: (filters: DiscoveryFilters, pagination: PaginationState) =>
    queryOptions({
      queryKey: INFLUENCER_KEYS.search(filters, pagination),
      queryFn: () => influencerAPIClient.searchInfluencers(filters, pagination),
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000,
    }),
  byIds: (ids: string[]) =>
    queryOptions({
      queryKey: INFLUENCER_KEYS.byIds(ids),
      queryFn: () => influencerAPIClient.getInfluencersByIds(ids),
      staleTime: 60 * 1000,
      enabled: ids.length > 0,
    }),
  detail: (influencer: Influencer | null) =>
    queryOptions({
      queryKey: INFLUENCER_KEYS.detail(influencer?.id ?? ""),
      queryFn: () => {
        if (!influencer)
          throw new Error("detail queryFn called with null influencer");
        return influencerAPIClient.getInfluencerById(influencer.id);
      },
      staleTime: 5 * 60 * 1000,
      enabled: influencer !== null,
    }),
  lists: () =>
    queryOptions({
      queryKey: INFLUENCER_KEYS.lists(),
      queryFn: () => influencerAPIClient.getLists(),
      staleTime: 60 * 1000,
    }),
  listMembers: (listId: string) =>
    queryOptions({
      queryKey: INFLUENCER_KEYS.listMembers(listId),
      queryFn: () => influencerAPIClient.getAllListMembers(listId),
      staleTime: 30 * 1000,
      enabled: listId !== "",
    }),
  listMembersPage: (listId: string, offset: number, limit: number) =>
    queryOptions({
      queryKey: INFLUENCER_KEYS.listMembersPage(listId, offset, limit),
      queryFn: () => influencerAPIClient.getListMembers(listId, offset, limit),
      placeholderData: keepPreviousData,
      staleTime: 30 * 1000,
      enabled: listId !== "",
    }),
};

export const INFLUENCER_MUTATIONS = {
  create: () => ({
    mutationFn: (params: CreateInfluencerParams) =>
      influencerAPIClient.createInfluencer(params),
  }),
};
